import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useSubscriptionStore } from '../../stores/subscription';
import { SUBSCRIPTION_TIERS, type SubscriptionTier } from '@profitos/types';
import { SkeletonBillingPage } from '../../components/Skeleton';

// Tier display configuration
const TIER_CONFIG: Record<SubscriptionTier, { name: string; description: string; color: string }> = {
  starter: {
    name: 'Starter',
    description: 'Perfect for getting started',
    color: 'bg-gray-100 text-gray-800',
  },
  growth: {
    name: 'Growth',
    description: 'For marketers ready to scale',
    color: 'bg-blue-100 text-blue-800',
  },
  pro: {
    name: 'Pro',
    description: 'Full-featured affiliate command center',
    color: 'bg-purple-100 text-purple-800',
  },
  scale: {
    name: 'Scale',
    description: 'Enterprise-grade power',
    color: 'bg-primary-100 text-primary-800',
  },
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`;
}

function formatLimit(value: number): string {
  return value === -1 ? 'Unlimited' : value.toLocaleString();
}

function UsageProgressBar({
  label,
  current,
  limit,
  unit = ''
}: {
  label: string;
  current: number;
  limit: number;
  unit?: string;
}): ReactNode {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const isWarning = percentage >= 80;
  const isLimit = percentage >= 100;

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">
          {unit === '$' ? formatCents(current) : current.toLocaleString()}
          {' / '}
          {isUnlimited ? 'Unlimited' : (unit === '$' ? formatCents(limit) : limit.toLocaleString())}
          {unit && unit !== '$' ? ` ${unit}` : ''}
        </span>
      </div>
      {!isUnlimited && (
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div
            className={`h-2 rounded-full transition-all ${
              isLimit ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-primary-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
}

function TierCard({
  tier,
  isCurrentTier,
  onSelect,
}: {
  tier: SubscriptionTier;
  isCurrentTier: boolean;
  onSelect: () => void;
}): ReactNode {
  const config = SUBSCRIPTION_TIERS[tier];
  const display = TIER_CONFIG[tier];

  return (
    <div
      className={`rounded-lg border-2 p-6 ${
        isCurrentTier ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${display.color}`}>
            {display.name}
          </span>
          <p className="mt-2 text-sm text-gray-600">{display.description}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-gray-900">
            ${config.displayPrice.monthly}
            <span className="text-base font-normal text-gray-500">/mo</span>
          </p>
          {config.displayPrice.annual > 0 && (
            <p className="text-sm text-gray-500">
              or ${config.displayPrice.annual}/mo billed annually
            </p>
          )}
        </div>
      </div>

      <ul className="mt-6 space-y-3">
        <li className="flex items-center text-sm text-gray-600">
          <svg className="mr-2 h-4 w-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Up to {formatLimit(config.revenueLimitCents === -1 ? -1 : config.revenueLimitCents / 100)} revenue tracking
        </li>
        <li className="flex items-center text-sm text-gray-600">
          <svg className="mr-2 h-4 w-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {formatLimit(config.aiContentLimit)} AI content pieces/month
        </li>
        <li className="flex items-center text-sm text-gray-600">
          <svg className="mr-2 h-4 w-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {formatLimit(config.affiliateLinksLimit)} affiliate links
        </li>
        <li className="flex items-center text-sm text-gray-600">
          <svg className="mr-2 h-4 w-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {config.dataRetentionDays === -1 ? 'Unlimited' : `${config.dataRetentionDays} days`} data retention
        </li>
      </ul>

      <button
        onClick={onSelect}
        disabled={isCurrentTier}
        className={`mt-6 w-full rounded-lg px-4 py-2 text-sm font-medium ${
          isCurrentTier
            ? 'cursor-not-allowed bg-gray-100 text-gray-500'
            : 'bg-primary-600 text-white hover:bg-primary-700'
        }`}
      >
        {isCurrentTier ? 'Current Plan' : 'Select Plan'}
      </button>
    </div>
  );
}

export function BillingPage(): ReactNode {
  const {
    subscription,
    limits,
    usage,
    warnings,
    invoices,
    isLoading,
    error,
    fetchSubscription,
    fetchUsage,
    fetchInvoices,
    createCheckoutSession,
    openBillingPortal,
    cancelSubscription,
    clearError,
  } = useSubscriptionStore();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    void fetchSubscription();
    void fetchUsage();
    void fetchInvoices();
  }, [fetchSubscription, fetchUsage, fetchInvoices]);

  const currentTier = (subscription?.tier ?? 'starter') as SubscriptionTier;

  const handleUpgrade = async (tier: SubscriptionTier): Promise<void> => {
    // Get price ID for monthly billing (could add billing cycle selector)
    const priceId = `price_${tier}_monthly`; // This should come from env/config
    const successUrl = `${window.location.origin}/settings/billing?success=true`;
    const cancelUrl = `${window.location.origin}/settings/billing?canceled=true`;

    const checkoutUrl = await createCheckoutSession(priceId, successUrl, cancelUrl);
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  const handleManageBilling = async (): Promise<void> => {
    const returnUrl = `${window.location.origin}/settings/billing`;
    const portalUrl = await openBillingPortal(returnUrl);
    if (portalUrl) {
      window.location.href = portalUrl;
    }
  };

  const handleCancelSubscription = async (): Promise<void> => {
    await cancelSubscription();
    setShowCancelConfirm(false);
  };

  // Show skeleton on initial load
  if (isLoading && !subscription) {
    return <SkeletonBillingPage />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="mt-1 text-gray-600">Manage your subscription and view usage</p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button onClick={clearError} className="text-red-500 hover:text-red-600">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings?.revenueApproaching && !warnings.revenueLimitReached && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Approaching Revenue Limit</h3>
              <p className="mt-1 text-sm text-yellow-700">
                You&apos;re at 80% of your revenue tracking limit. Consider upgrading to continue tracking.
              </p>
            </div>
          </div>
        </div>
      )}

      {warnings?.revenueLimitReached && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Revenue Limit Reached</h3>
              <p className="mt-1 text-sm text-red-700">
                You&apos;ve reached your revenue tracking limit. Upgrade now to continue tracking.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Summary */}
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
            <div className="mt-2 flex items-center gap-3">
              <span className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${TIER_CONFIG[currentTier].color}`}>
                {TIER_CONFIG[currentTier].name}
              </span>
              {subscription?.status && (
                <span className={`text-sm ${
                  subscription.status === 'active' ? 'text-green-600' :
                  subscription.status === 'past_due' ? 'text-red-600' :
                  'text-gray-600'
                }`}>
                  {subscription.status === 'active' ? 'Active' :
                   subscription.status === 'past_due' ? 'Past Due' :
                   subscription.status}
                </span>
              )}
            </div>
            {subscription?.cancelAtPeriodEnd && (
              <p className="mt-2 text-sm text-orange-600">
                Your subscription will cancel at the end of the current period.
              </p>
            )}
          </div>
          {subscription?.stripeCustomerId && (
            <button
              onClick={() => void handleManageBilling()}
              disabled={isLoading}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Manage Billing
            </button>
          )}
        </div>
      </div>

      {/* Usage Section */}
      {usage && limits && (
        <div className="rounded-lg border bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">Current Usage</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <UsageProgressBar
              label="Tracked Revenue"
              current={usage.trackedRevenueCents}
              limit={limits.revenueLimitCents}
              unit="$"
            />
            <UsageProgressBar
              label="AI Content Generated"
              current={usage.aiContentGenerated}
              limit={limits.aiContentLimit}
              unit="pieces"
            />
            <UsageProgressBar
              label="Affiliate Links"
              current={usage.affiliateLinksCount}
              limit={limits.affiliateLinksLimit}
            />
            <UsageProgressBar
              label="Affiliate Programs"
              current={usage.affiliateProgramsCount}
              limit={limits.affiliateProgramsLimit}
            />
          </div>
        </div>
      )}

      {/* Upgrade Options */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Choose Your Plan</h2>
        <p className="mt-1 text-sm text-gray-600">
          Select a plan that fits your needs. Upgrade or downgrade anytime.
        </p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {(['starter', 'growth', 'pro', 'scale'] as SubscriptionTier[]).map((tier) => (
            <TierCard
              key={tier}
              tier={tier}
              isCurrentTier={tier === currentTier}
              onSelect={() => void handleUpgrade(tier)}
            />
          ))}
        </div>
      </div>

      {/* Billing History */}
      {invoices.length > 0 && (
        <div className="rounded-lg border bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">Billing History</h2>
          <div className="mt-4 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Invoice
                  </th>
                  <th className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Date
                  </th>
                  <th className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Amount
                  </th>
                  <th className="py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="whitespace-nowrap py-4 text-sm font-medium text-gray-900">
                      {invoice.number ?? invoice.id.slice(0, 8)}
                    </td>
                    <td className="whitespace-nowrap py-4 text-sm text-gray-500">
                      {new Date(invoice.created * 1000).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap py-4 text-sm text-gray-900">
                      {formatCents(invoice.amountPaid || invoice.amountDue)} {invoice.currency.toUpperCase()}
                    </td>
                    <td className="whitespace-nowrap py-4 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : invoice.status === 'open'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {invoice.status ?? 'Unknown'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-4 text-right text-sm">
                      <div className="flex justify-end gap-2">
                        {invoice.hostedInvoiceUrl && (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800"
                          >
                            View
                          </a>
                        )}
                        {invoice.invoicePdf && (
                          <a
                            href={invoice.invoicePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800"
                          >
                            PDF
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cancel Section */}
      {subscription?.stripeSubscriptionId && !subscription.cancelAtPeriodEnd && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="text-lg font-semibold text-red-900">Cancel Subscription</h3>
          <p className="mt-2 text-sm text-red-700">
            If you cancel, you&apos;ll still have access until the end of your current billing period.
            You can resubscribe anytime.
          </p>
          {!showCancelConfirm ? (
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="mt-4 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
            >
              Cancel Subscription
            </button>
          ) : (
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => void handleCancelSubscription()}
                disabled={isLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isLoading ? 'Canceling...' : 'Yes, Cancel'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Keep Subscription
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
